export type ExperimentName = `experiment.${string}`
type Experiment = {
  defaultValue: 'noVariation'
  urlPatterns: Array<string>
}

/**
 * This dictionary holds the configuration for all the experiments currently
 * being assigned by the experiments worker.
 *
 * @property defaultValue - Must be set to noVariation for all of them.
 * @property urlPatterns - Allows you to scope each experiment to their
 * associated set of pages. Those patterns are used on the worker side
 * to know if the experiment will be used to process the current request
 * or not. It helps us improving our cache hit ratio, and thus the
 * overall server-side performance.
 */
export const config = {
  'experiment.globalAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.homeAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/?$' /* Home page */],
  },
  'experiment.landingAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* Landing page */],
  },
  'experiment.productAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.checkoutAA': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.homeCMSPageName': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/?$' /* Home page - permanent AB test */],
  },
  'experiment.goodDealsEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - GoodDeal */,
    ],
  },
  'experiment.recyclingEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - Recycling */,
    ],
  },
  'experiment.holidaysEventPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/e/.*$' /* Event pages - permanent AB test - Holidays */,
    ],
  },
  'experiment.homeBuybackPageName': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages - permanent AB test */,
    ],
  },
  // https://backmarket.atlassian.net/wiki/spaces/2C/pages/4526833841/Filtering+cart+insurances+monthly+only+vs+upfront+only
  'experiment.monthlyFilterExperiment': {
    defaultValue: 'noVariation',
    /** Variations:
     * noFilter
     * monthlyOnly
     * upfrontOnly
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  // https://backmarket.atlassian.net/wiki/spaces/SR/pages/5334073749/PRD+Gift+Cards+POC?focusedCommentId=5750030399
  'experiment.giftCards': {
    defaultValue: 'noVariation',
    /** Variations:
     * withGiftCards
     * withoutGiftCards */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  // https://backmarket.atlassian.net/wiki/spaces/2C/pages/5329682450/Noble+Mobile+x+Visible
  'experiment.nobleMobile': {
    defaultValue: 'noVariation',
    /** Variations:
     * withNobleMobile
     * withoutNobleMobile */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  // https://backmarket.atlassian.net/wiki/spaces/2C/pages/5173084653/PRD+BM-branded+mobile+plans+fake+door+test+1
  'experiment.mobilePlanFakeDoor': {
    /** Variations:
     * withoutMobilePlan
     * withBackMarketMobilePlan
     * withExternalTelcoMobilePlan */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.enableScalapayIT': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages (in the footer) */],
  },
  'experiment.enableOney10x12xFR': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages (footer is not impacted by this test) */,
    ],
  },
  'experiment.konbiniThresholdsJP': {
    defaultValue: 'noVariation',
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages (footer is not impacted by this test) */,
    ],
  },
  'experiment.reviewSellerAndFilters': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * showSeller */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
      '^/[a-z]{2}-[a-z]{2}/r/p/.*' /* Product Reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/r/.*' /* Reviews pages */,
      '^/[a-z]{2}-[a-z]{2}/r/s/.*' /* Seller Reviews pages */,
    ],
  },
  'experiment.reviewAmount': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * tenReviews
     * sevenReviews */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
    ],
  },
  'experiment.reviewsAiSummary': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutAIReview
     * withAIReview
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.reviewsAiTags': {
    defaultValue: 'noVariation',
    /** Variations:
     * noVariation
     * withoutAITags
     * withAITags
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.paymentFormVersion': {
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.checkoutCollectionPointOption': {
    /** Variations:
     * collectionPointEnabled
     * collectionPointDisabled
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.checkoutResultNew': {
    /** Variations:
     * resultNew
     * resultLegacy
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
  'experiment.serviceFeeGrid': {
    /** Variations:
     * newServiceFeeGrid
     * currentServiceFeeGrid
     */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },

  'experiment.countrySwitchRedirection': {
    /** Variations:
     * withRedirection
     * withoutRedirection */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.comparisonV2': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/compare/.*' /* Comparison pages */],
  },
  'experiment.deliveryDateAdjustment': {
    /** Variations:
     * withAdjustment
     * withoutAdjustment */
    defaultValue: 'noVariation',
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.flashSales': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.sellerFlashSales': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },

  'experiment.newGradePicker': {
    defaultValue: 'noVariation',
    /** Variations:
     * withNewGradePicker
     * withoutNewGradePicker */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages */],
  },
  'experiment.newOfferPageV3': {
    defaultValue: 'noVariation',
    /** Variations:
     * withGraph
     * withoutGraph */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages */],
  },
  'experiment.resaleBankStep': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBankStep
     * withoutBankStep */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/buyback/.*' /* All Buyback Pages */],
  },
  'experiment.ppNewATCButtonDesign': {
    defaultValue: 'noVariation',
    /** Variations:
     * withoutNewATCButtonDesign
     * withNewATCButtonDesign */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppNewPriceDesign': {
    defaultValue: 'noVariation',
    /** Variations:
     * withoutNewPriceDesign
     * withNewPriceDesign */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppReviewsInCarousel': {
    defaultValue: 'noVariation',
    /** Variations:
     * withoutReviewsInCarousel
     * withReviewsInCarousel */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
    ],
  },
  'experiment.promoCodeInCart': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.hidePlpBanner': {
    defaultValue: 'noVariation',
    /** Variations:
     * with
     * without */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */],
  },
  'experiment.ppVerifiedRefurbishedCarouselV2': {
    defaultValue: 'noVariation',
    /** Variations:
     * withNewDesign
     * withOldDesign */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP page */,
    ],
  },
  'experiment.stickerInCarousel': {
    defaultValue: 'noVariation',
    /** Variations:
     * withSticker
     * withoutSticker */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppJapanBlackFridaySpecial': {
    defaultValue: 'noVariation',
    /** Variations:
     * withBlackFridaySpecial
     * withoutBlackFridaySpecial */
    urlPatterns: ['^/ja-jp/p/.*' /* Product page */],
  },
  'experiment.ppUpgradeForLess': {
    defaultValue: 'noVariation',
    /** Variations:
     * withUpgrade
     * withoutUpgrade */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.cmsPromotionalBanner': {
    defaultValue: 'noVariation',
    /** Variations:
     * controlText
     * variantText
     */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages (above Header) */],
  },
  'experiment.ppExpressReplacement': {
    defaultValue: 'noVariation',
    /** Variations:
     * withExpressReplacement
     * withoutExpressReplacement */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppOrderColorByPrice': {
    defaultValue: 'noVariation',
    /** Variations:
     * orderedByPrice
     * orderedByBack */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppVideosInCarousel': {
    defaultValue: 'noVariation',
    /** Variations:
     * withVideosInCarousel
     * withoutVideosInCarousel */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.skinnyOrPromotionalBanner': {
    defaultValue: 'noVariation',
    /** Variations:
     * skinnyBanner
     * promotionalBanner
     */
    urlPatterns: [
      '^/[a-z]{2}-[a-z]{2}' /* Home page */,
      '^/[a-z]{2}-[a-z]{2}/l/.*' /* PLP */,
      '^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */,
      '^/[a-z]{2}-[a-z]{2}/search' /* SearchResultPage */,
      '^/[a-z]{2}-[a-z]{2}/s/.*' /* SellerPage */,
    ],
  },
  'experiment.ppTelcoCalculator': {
    defaultValue: 'noVariation',
    /** Variations:
     * withCalculator
     * withouCalculator */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.ppAutomatedReviewsInCarousel': {
    defaultValue: 'noVariation',
    /** Variations:
     * withAutomatedReviews
     * withoutAutomatedReviews */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/p/.*' /* Product page */],
  },
  'experiment.aiShoppingAssistant': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.enableEsUsContent': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}.*' /* All pages */],
  },
  'experiment.inputRedesign': {
    defaultValue: 'noVariation',
    /** Variations:
     * enabled
     * disabled */
    urlPatterns: ['^/[a-z]{2}-[a-z]{2}/f2/.*' /* Checkout pages */],
  },
} satisfies Record<ExperimentName, Experiment>
